<template>
    <ProductTypeDefinition
        :product="product"
        @suppliers="setSuppliers"
        @done="init"
    >
        <div>
            <Breadcrumbs/>
            <SaveAndProceedOrRefusePrompt
                ref="saveAndProceedPrompt"
                @rejectAndProceed="rejectAndGotoSendRequest"
                @saveAndProceed="saveAndGotoSendRequest"
            />
            <b-overlay
                variant="white"
                :show="showLoading"
                spinner-variant="primary"
                blur="0"
                opacity=".85"
                rounded="sm"
            >
                <b-card ref="productCard">
                    <b-row>
                        <b-col md="12" class="mb-2">
                            <h2 class="float-left pr-1 mb-1">
                                {{ $t(getProductTypeTranslationKey()) }}: {{ getProductTitle() }} {{ suppliersVerification }}
                            </h2>
                            <SaveAndClose
                                @save="save"
                                @close="close"
                                @sendRequest="sendRequest"
                                @remove="removeProduct"
                                @back="back"
                                @makeSCIPNotification="makeSCIPNotification"
                                class="float-right"
                                :show-remove="canRemove"
                                :show-close="isCloseVisible"
                                :show-send-request="showSendRequestButton"
                                :show-back="isBackVisible"
                                :scip-notification-facts="scipNotificationFacts"
                                :is-scip-notification-button-visible="isScipNotificationButtonVisible"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="12">
                            <b-tabs content-class="mt-2" justified v-model="tabIndex">
                                <b-tab
                                    :title="$t('Component(ProductCardTabs):general-info-label')"
                                    active
                                >
                                    <GeneralInfo
                                        v-if="renderPhase >= 1"
                                        :product="product"
                                        :id="id"
                                        :suppliers="suppliers"
                                        :add-new-component-button="showAddNewComponentButton"
                                        :has-concern-elements="isProductHasConcernElements"
                                        :embedded="embedded"
                                        :product-description-document="productDescription"
                                        :is-rohs-enabled="isRohsEnabled"
                                        :status-explanation-revision="statusExplanationRevision"
                                        @characteristicsChange="productCharacteristicsChange"
                                        @productDescriptionChange="productDescriptionChange"
                                        @commentChange="commentChange"
                                        @legislationDataChange="updateLegislationSettings"
                                        @includedProductClick="onIncludedProductClick"
                                        @suppliersChange="suppliersChange"
                                        @addProductAndIncludeClick="onAddProductAndIncludeClick"
                                    />
                                </b-tab>
                                <b-tab
                                    v-if="isRegulationQuestionsTabVisible"
                                    :title="$t('Component(ProductCardTabs):regulation-questions-label')"
                                >
                                    <Overlay :show="legislationQuestionnaireOverlayLoading">
                                        <RegulationQuestions
                                            v-if="renderPhase >= 2"
                                            :groups="legislationQuestionnaire"
                                            :legislations="applicableLegislations"
                                            :answers="legislationQuestionAnswers"
                                            @answersChange="onLegislationQuestionAnswersChange"
                                        />
                                    </Overlay>
                                </b-tab>
                                <b-tab
                                    v-if="isFMDTabVisible"
                                    :title="$t('Component(ProductCardTabs):fmd-label')"
                                >
                                    <Overlay :show="loadingFMDDeclarations">
                                        <FMDTab
                                            :product-id="id"
                                            :declarations="fmdDeclarations"
                                            :product-weight="characteristicsWeight"
                                            :get-substance-lists="fetchSharedFmdSubstanceLists"
                                            @declarationChange="saveFMDAndMarkIncludedSubstances"
                                            @weightChange="onWeightChange"
                                        />
                                    </Overlay>
                                </b-tab>
                                <b-tab
                                    v-if="isFMDTabVisible"
                                    :title="$t('Component(ProductCardTabs):concern-elements-label')"
                                >
                                    <SubstanceListsWithQuestions
                                        v-if="renderPhase >= 2"
                                        :product="product"
                                        :legislations="legislations.map(l => l.id)"
                                        :included-substances="fmdIncludedSubstances"
                                        :included-list-ids="fmdIncludedListIds"
                                        :loading="loadingFMDDeclarations || fmdIncludedListsLoading"
                                        :collapse-questionless-referenced-lists="true"
                                        @listQuestionStateUpdated="listQuestionStateUpdated"
                                    />
                                </b-tab>
                                <b-tab
                                    v-if="isConcernElementsTabVisible"
                                    :title="$t('Component(ProductCardTabs):concern-elements-label')"
                                    :disabled="!isConcernElementsTabAccessable"
                                    @click="onConcernElementsTabClick"
                                >
                                    <ConcernElements
                                        v-if="renderPhase >= 2 && concernElementsInitialized"
                                        :product="product"
                                        :id="id"
                                        :company-has-information="product.informationOwnerSettings.weHaveInformation"
                                        :included-substances="includedSubstances"
                                        :excluded-substances="excludedSubstances"
                                        :legislations="legislations.map(l => l.id)"
                                        @listQuestionStateUpdated="listQuestionStateUpdated"
                                        @informationOwnerSettingsChanged="informationOwnerSettingsChanged"
                                        @include="includeConcernSubstance"
                                        @exclude="excludeConcernSubstance"
                                        @undefined="makeSubstanceUndefined"
                                    />
                                </b-tab>
                                <b-tab
                                    v-if="isCompositionTabVisible"
                                    :title="$t('Component(ProductCardTabs):composition-label')"
                                >
                                    <Composition
                                        v-if="renderPhase >= 3"
                                        :product="product"
                                        :id="id"
                                        :concern-substances="includedSubstances"
                                        :excluded-substances="excludedSubstances"
                                        :show-components-substances="isComplexProduct"
                                        :compact-view="compositionCompactView"
                                        :legislations="legislations.map(l => l.id)"
                                        @hasConcernSubstances="hasConcernSubstances"
                                        @substancePropertiesChanged="onSubstancePropertiesChange"
                                        @substanceDetailsChange="onSubstanceDetailsChange"
                                        @include="includeConcernSubstance"
                                        @exclude="excludeConcernSubstance"
                                    />
                                </b-tab>
                                <b-tab
                                    :title="$t('Component(ProductCardTabs):verification-label')"
                                    v-if="isVerificationTabVisible"
                                >
                                    <Verification
                                        v-if="renderPhase >= 4"
                                        ref="verification"
                                        :compact-view="verificationCompactView"
                                        :product="product"
                                        :id="id"
                                        :doc="declarationsOfConformity"
                                        :compliant-statement="compliantStatement"
                                        :evidence="evidenceReports"
                                        :other="otherVerificationDocuments"
                                        :safe-use="safeUseInstructions"
                                        :disassembling="disassemblingInstructions"
                                        :is-safe-use-applicable="safeUseApplicable"
                                        @safeUseApplicableChange="safeUseApplicableChange"
                                        @addDoc="addDoC"
                                        @removeDoc="removeDoC"
                                        @addCompliantStatement="addCompliantStatement"
                                        @removeCompliantStatement="removeCompliantStatement"
                                        @addReport="addReport"
                                        @removeReport="removeReport"
                                        @safeUseChange="safeUseChange"
                                        @disassemblingChange="disassemblingChange"
                                        @refreshDocuments="fetchDocuments(true)"
                                    />
                                </b-tab>
                                <b-tab
                                    :title="$t('Component(ProductCardTabs):supplier-documents-label')"
                                    v-if="isSupplierDocumentsTabVisible"
                                >
                                    <SupplierVerification
                                        v-if="renderPhase >= 4"
                                        :product="product"
                                        :id="id"
                                        :compact-view="verificationCompactView"
                                    />
                                </b-tab>
                                <b-tab
                                    :title="$t('Component(ProductCardTabs):statistics-label')"
                                    v-if="isStatisticsTabVisible"
                                >
                                    <ProductStatisticsDashboard
                                        v-if="renderPhase >= 4"
                                        :productId="id"
                                    />
                                </b-tab>
                            </b-tabs>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <SaveAndClose
                                @save="save"
                                @close="close"
                                @sendRequest="sendRequest"
                                @remove="removeProduct"
                                @back="back"
                                class="float-right"
                                :show-remove="canRemove"
                                :show-close="isCloseVisible"
                                :show-send-request="showSendRequestButton"
                                :show-back="isBackVisible"
                            />
                        </b-col>
                    </b-row>
                </b-card>
            </b-overlay>
        </div>
    </ProductTypeDefinition>
</template>
<script>
import { BTabs, BTab, BCard, BRow, BCol, BOverlay, BLink } from 'bootstrap-vue'
import {ProductType} from "@/libs/product";
import {GeneralInfo, Composition, Verification, SaveAndClose, ProductCardMixins, ConcernElements} from "@/views/product/components/product-card";
import Ripple from 'vue-ripple-directive'
import {SaveAndProceedOrRefusePrompt} from "@core/components/common";
import 'animate.css'
import {clearCachedProduct, ProductBreadcrumbs} from "@core/domain/product";
import Breadcrumbs from "@/views/product/Breadcrumbs.vue";
import ProductTypeDefinition from "@core/components/product/ProductTypeDefinition.vue";
import SupplierVerification from "@/views/product/components/product-card/SupplierVerification.vue";
import ProductStatisticsDashboard from "@/views/product/components/product-card/ProductStatisticsDashboard.vue";
import Overlay from "@core/components/common/Overlay.vue";
import FMDTab from "@/views/product/components/product-card/fmd/FMDTab.vue";
import LegislationQuestionnaireMixin from '@/views/product/components/product-card/LegislationQuestionnaireMixin'
import RegulationQuestions from '@/views/product/components/product-card/RegulationQuestions.vue'
import {
    FmdDerivedSubstanceListsMixin,
    SubstanceListsWithQuestions,
} from "@/views/product/components/product-card/fmd";

const raf = () => new Promise(r => requestAnimationFrame(r))

export default {
    mixins: [FmdDerivedSubstanceListsMixin, LegislationQuestionnaireMixin],
    components: {
        RegulationQuestions,
        FMDTab, Overlay,
        ProductStatisticsDashboard, SupplierVerification, ProductTypeDefinition, Breadcrumbs, SubstanceListsWithQuestions,
        BOverlay, BLink, SaveAndProceedOrRefusePrompt, BCard, BTabs, BTab, BRow, BCol,
        GeneralInfo, ConcernElements, Composition, Verification, SaveAndClose,
    },

    directives: {
        Ripple,
    },

    props: {
        id: {},
        embedded: {
            type: Boolean,
            default: false
        },
        topLevelProduct: {
            default: null
        },
        keepBreadcrumbs: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            _phaseTimer: null,
            maxRenderPhase: 5,
            renderPhase: 0,
            showLoading: true,
            verificationCompactView: false,
            compositionCompactView: false,
            tabIndex: 0,
            concernElementsInitialized: false,

            ...ProductCardMixins.data
        };
    },

    methods: {
        sendRequest() {
            if (this.hasUnSavedChanges()) {
                this.$refs.saveAndProceedPrompt.show();
            } else {
                this.gotoSendRequest();
            }
        },

        saveAndGotoSendRequest(close) {
            close();
            this.save(
                false,
                false,
                true
            ).then(() => this.gotoSendRequest());
        },

        rejectAndGotoSendRequest() {
            clearCachedProduct(this.id);
            this.gotoSendRequest();
        },

        gotoSendRequest() {
            if (this.$store.getters.showSendEmailSettingsStep) {
                this.$router.push({
                    name: 'request-email-options',
                    query: {
                        n: 'product-send-request',
                        nid: this.id,
                        b: 'product-card',
                        bid: this.id
                    }
                });
            } else {
                this.$router.push({
                    name: 'product-send-request',
                    params: {id: this.id, product: this.product},
                    props: {product: this.product}
                });
            }
        },

        close() {
            this.$router.push({name: 'product-overview'});
        },

        back() {
            ProductBreadcrumbs.back();
        },

        makeSCIPNotification() {
            this.$router.push({
                name: 'product-scip-notification',
                params: {id: this.id}
            })
        },

        onAddProductAndIncludeClick() {},

        onIncludedProductClick(includedProduct) {
            this.$router.push({
                name: 'product-card',
                params: {
                    id: includedProduct.id + '',
                    embedded: true,
                    keepBreadcrumbs: true
                },
                props: {}
            });
        },

        getProductTypeTranslationKey() {
            switch (this.product.type) {
                case ProductType.GROUP:
                    return 'Component(Text):product-type-group'

                case ProductType.OWN:
                    return 'Component(Text):product-type-own'

                case ProductType.PURCHASED_UNDEFINED_SUPPLIER:
                case ProductType.PURCHASED:
                    return 'Component(Text):product-type-purchased'

                case ProductType.COMPONENT_TO_COMPLEX_PURCHASED_PRODUCT:
                case ProductType.COMPONENT_TO_PURCHASED_PRODUCT:
                    return 'Component(Text):product-type-component'

                case ProductType.FICTITIOUS:
                    return 'Component(Text):product-type-fictitious'

                default:
                    return '';
            }
        },
        getProductTitle() {
            if (this.product.names.length > 0) {
                return this.product.names[0].value;
            }

            return '';
        },

        flushData() {
            this.includedSubstances = [];
            this.excludedSubstances = [];
            this.fmdIncludedSubstances = [];
            this.fmdIncludedListIds = [];
            this.suppliers = [];
            this.evidenceReports = [];
            this.otherVerificationDocuments = [];
            this.declarationsOfConformity = [];
            this.safeUseInstructions = {
                description: '',
                    file: null
            };
            this.disassemblingInstructions = {
                description: '',
                file: null
            };
            this.safeUseApplicable = null;
            this.needToSaveSubstances = false;
        },

        ...ProductCardMixins.methods,

        getProductId() {
            return this.id;
        },

        saveFMDAndMarkIncludedSubstances(change) {
            this.onFMDChange(change);
            this.syncFmdDerivedSubstanceLists(this.fmdDeclarations);
        },

        fetchSubstancesAndDocuments() {
            const tasks = [this.fetchDocuments()];

            if (this.isFMDTabVisible) {
                tasks.push(
                    this.fetchFMDDeclarations(true).then(() => {
                        return this.syncFmdDerivedSubstanceLists(this.fmdDeclarations);
                    })
                );
            } else {
                tasks.push(this.fetchSubstances());
            }

            return Promise.all(tasks).then(this.updateProductState);
        },

        initProductBreadcrumbs() {
            const productId = this.id;
            ProductBreadcrumbs.push(
                'Product' + productId,
                this.getProductTitle(),
                () => {
                    this.$router.push({
                        name: 'product-card',
                        params: {id: productId}
                    })
                }
            );
        },

        removeProduct() {
            this.$swal({
                title: this.$t('Page(ProductCard):Component(RemoveProductPrompt):title'),
                text: this.$t('Page(ProductCard):Component(RemoveProductPrompt):text'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.$t('Page(ProductCard):Component(RemoveProductPrompt):delete-btn'),
                cancelButtonText: this.$t('Component(Button):cancel'),
                customClass: {
                    confirmButton: 'btn btn-danger',
                    cancelButton: 'btn btn-outline-secondary ml-1',
                },
                buttonsStyling: false,
            }).then(result => {
                if (result.isConfirmed) {
                    this.showLoading = true;
                    this.remove().then(() => {
                        this.showLoading = false;
                        this.$swal({
                            icon: 'success',
                            title: this.$t('Page(ProductCard):Component(RemoveProductAck):title'),
                            text: this.$t('Page(ProductCard):Component(RemoveProductAck):text'),
                            timer: 1000,
                            customClass: {
                                confirmButton: 'btn btn-primary',
                            },
                        })
                        this.close();
                    });
                }
            })
        },
        onResize() {
            this.verificationCompactView = this.$refs.productCard.offsetWidth < 1500;
            this.compositionCompactView = this.$refs.productCard.offsetWidth < 1145;
        },
        runPhases(ms = 100) {
            const tick = () => {
                if (this.renderPhase >= this.maxRenderPhase) {
                    return;
                }

                this.renderPhase++
                this._phaseTimer = setTimeout(tick, ms)
            }

            this._phaseTimer = setTimeout(tick, ms)
        },
        onConcernElementsTabClick() {
            this.concernElementsInitialized = true;
        }
    },

    async mounted() {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.$refs.productCard);

        await this.$nextTick();
        await raf();
        this.renderPhase++;

        this.runPhases(100);
    },

    beforeDestroy() {
        clearTimeout(this._phaseTimer)
        this.resizeObserver?.disconnect()
    },
    created() {
        this.init();
    },

    computed: {
        ...ProductCardMixins.computed,

        characteristicsWeight() {
            if (!this.product.characteristics) {
                return null;
            }
            return this.product.characteristics.weight || null;
        },

        showSendRequestButton() {
            if (!this.$can('view', 'button:send-request')) {
                return false;
            }
            return (
                this.product.type === ProductType.GROUP
                || this.product.type === ProductType.OWN
                || this.product.type === ProductType.PURCHASED
                || this.product.type === ProductType.FICTITIOUS
            )
        },

        canRemove() {
            return this.product.canDelete;
        },
        isBackVisible() {
            return ProductBreadcrumbs.count() > 2;
        },
        isScipNotificationButtonVisible() {
            return this.$can('view', 'button:make-scip-notification') && this.isScipEnabled;
        },
        isCloseVisible() {
            return ProductBreadcrumbs.count() === 2;
        },
        showAddNewComponentButton() {
            return this.product.isComplex || (this.product.isComplex === false && true !== this.product.hasConcernSubstances);
        },

        suppliersVerification() {
            return this.isSupplierDocumentsTabVisible ? '(' + this.$t('Component(ProductCardTabs):supplier-documents-label') + ')' : '';
        }
    },

    watch: {
        id(newValue, oldValue) {
            this.showLoading = true;
            this.flushData();
            this.init();
            this.tabIndex = 0;
            this.concernElementsInitialized = false;
        }
    }
};
</script>
